/**
 * `InvestorKycStatus`, and what counts as approved.
 *
 * `CONDITIONALLY_APPROVED` is a fourth outcome an admin can set on any of the
 * three checks — identity, AML and document — independently of the other two.
 * The backend accepts it wherever it used to require `APPROVED`: a trade that
 * needs an approved document and AML check takes either approved value for
 * each, and still rejects a pending or rejected one.
 *
 * So anything that *gates* on approval has to ask for both values, or the
 * dashboard disables an action the backend would have allowed. Anything that
 * *displays* the status keeps them apart — the whole point of the new value is
 * that an operator can see the approval was conditional.
 *
 * It is stored and returned as itself; the backend never folds it back into
 * `APPROVED`, and neither do the denormalized `kycStatus.identityCheck`,
 * `.amlCheck` and `.documentCheck` fields.
 */

export const CONDITIONALLY_APPROVED = 'CONDITIONALLY_APPROVED';

/** Both approval states, for a filter that means "approved either way". */
export const APPROVED_KYC_STATUSES = ['APPROVED', CONDITIONALLY_APPROVED];

/**
 * Does this check count as approved?
 *
 * Use this instead of `status === 'APPROVED'` for every permission, gate or
 * "is this user cleared" question. Uppercased first because these values reach
 * the dashboard from several queries and a couple of them are denormalized
 * copies rather than the enum itself.
 */
export const isApprovedKycStatus = status =>
  APPROVED_KYC_STATUSES.includes(String(status || '').toUpperCase());
